import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, ChevronDown, ChevronUp, CalendarDays, Star, HelpCircle, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { TASK_CATEGORIES, getAllTasks, sortTasks, updateTask, deleteTask } from "@/lib/tasks";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useTranslation } from "@/i18n";
import { toast } from "sonner";

function fmtDate(iso) { if (!iso) return "—"; const [y,m,d]=iso.split("-"); return `${d}.${m}.${y}`; }

export default function Tasks() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [tab, setTab] = useState("work");
  const [tasks, setTasks] = useState(() => getAllTasks());
  const [archiveOpen, setArchiveOpen] = useState({});
  const [confirm, setConfirm] = useState(null); // {task, newStatus}
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    const r = () => setTasks(getAllTasks());
    window.addEventListener("chronicles:tasks-changed", r);
    return () => window.removeEventListener("chronicles:tasks-changed", r);
  }, []);

  const { open, archived } = useMemo(() => {
    const inCat = tasks.filter((t) => t.category === tab);
    return { open: sortTasks(inCat.filter((t) => !t.archived)), archived: sortTasks(inCat.filter((t) => t.archived)) };
  }, [tasks, tab]);

  const statusLabel = (s) =>
    s === "success" ? t("tasks.statusSuccess") : s === "failed" ? t("tasks.statusFailed") : t("tasks.statusUnknown");

  const setStatus = (item, next) => {
    if ((item.status || "unknown") === next) return;
    if (next === "unknown") { updateTask(item.id, { status: "unknown" }); toast.success(t("tasks.toastStatusUpdated")); return; }
    setConfirm({ task: item, newStatus: next });
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    deleteTask(deleteTarget.id);
    toast.success(t("tasks.toastTaskDeleted"));
    setDeleteTarget(null);
  };

  const applyConfirm = (archive) => {
    if (!confirm) return;
    updateTask(confirm.task.id, { status: confirm.newStatus, archived: archive ? true : confirm.task.archived });
    toast.success(archive ? t("tasks.toastTaskArchived") : t("tasks.toastStatusUpdated"));
    setConfirm(null);
  };

  return (
    <div className="min-h-screen bg-background transition-colors duration-300 flex flex-col">
      <header className="sticky top-0 z-20 backdrop-blur-xl bg-card/80 border-b border-border">
        <div className="mx-auto max-w-2xl px-4 py-3 flex items-center gap-2">
          <Link to="/" className="h-9 w-9 rounded-full hover:bg-slate-100 flex items-center justify-center" data-testid="tasks-back" aria-label={t("tasks.backToHome")}><ArrowLeft className="h-4 w-4" /></Link>
          <div className="flex-1"><p className="text-[10px] uppercase tracking-[0.22em] text-slate-500">{t("tasks.title")}</p><p className="font-display text-base font-semibold">{t(`taskCategories.${tab}`)}</p></div>
          <LanguageSwitcher />
          <button onClick={() => navigate("/tasks/new")} className="h-9 w-9 rounded-full bg-slate-900 text-white flex items-center justify-center" data-testid="tasks-create-btn" aria-label={t("tasks.newTask")}><Plus className="h-4 w-4" /></button>
        </div>
        <div className="mx-auto max-w-2xl px-4 pb-3 flex gap-1.5 overflow-x-auto" data-testid="tasks-tabs">
          {TASK_CATEGORIES.map((c) => (
            <button key={c.id} onClick={() => setTab(c.id)} className={["shrink-0 h-9 px-3.5 rounded-full text-sm border transition", tab===c.id?"bg-slate-900 text-white border-slate-900":"bg-white border-slate-300 text-slate-700"].join(" ")} data-testid={`tasks-tab-${c.id}`}>
              <span className="mr-1">{c.emoji}</span>{t(`taskCategories.${c.id}`)}
            </button>
          ))}
        </div>
      </header>

      <main className="flex-1 mx-auto w-full max-w-2xl px-4 py-4 pb-24">
        <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-2">{t("tasks.openTasks")}</p>
        <ul className="space-y-2" data-testid="tasks-open-list">
          {open.length===0 ? <li className="text-sm text-slate-500 py-4 text-center">{t("tasks.noOpen")}</li> : open.map((item) => <TaskRow key={item.id} t={item} tr={t} onSetStatus={(s)=>setStatus(item,s)} onDelete={()=>setDeleteTarget(item)} onEdit={()=>navigate(`/tasks/edit/${item.id}`)} />)}
        </ul>

        {archived.length>0 && (
          <div className="mt-6">
            <button onClick={()=>setArchiveOpen((s)=>({...s,[tab]:!s[tab]}))} className="w-full flex items-center justify-between text-[11px] uppercase tracking-[0.18em] text-slate-500 py-2" data-testid="tasks-archive-toggle">
              <span>{t("tasks.archivedHeading", { label: t(`taskCategories.${tab}`), count: archived.length })}</span>
              {archiveOpen[tab]?<ChevronUp className="h-3.5 w-3.5"/>:<ChevronDown className="h-3.5 w-3.5"/>}
            </button>
            {archiveOpen[tab] && <ul className="space-y-2 mt-1" data-testid="tasks-archived-list">{archived.map((item)=> <TaskRow key={item.id} t={item} tr={t} archived onSetStatus={(s)=>setStatus(item,s)} onDelete={()=>setDeleteTarget(item)} onEdit={()=>navigate(`/tasks/edit/${item.id}`)} />)}</ul>}
          </div>
        )}
      </main>

      <Dialog open={!!confirm} onOpenChange={(o)=>!o&&setConfirm(null)}>
        <DialogContent className="rounded-2xl sm:max-w-sm" data-testid="tasks-archive-dialog">
          <DialogHeader><DialogTitle>{t("tasks.archiveTitle")}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">{t("tasks.statusWillChange", { status: statusLabel(confirm?.newStatus) })}</p>
          <DialogFooter className="gap-2 sm:gap-2 flex-col sm:flex-row">
            <Button variant="ghost" onClick={()=>setConfirm(null)} data-testid="tasks-confirm-cancel">{t("tasks.cancel")}</Button>
            <Button variant="outline" className="rounded-full" onClick={()=>applyConfirm(false)} data-testid="tasks-confirm-keep">{t("tasks.keepInList")}</Button>
            <Button className="rounded-full bg-slate-900" onClick={()=>applyConfirm(true)} data-testid="tasks-confirm-archive">{t("tasks.archive")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(o)=>!o&&setDeleteTarget(null)}>
        <DialogContent className="rounded-2xl sm:max-w-sm" data-testid="tasks-delete-dialog">
          <DialogHeader><DialogTitle>{t("tasks.deleteTitle")}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">{t("tasks.deleteDesc")}</p>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="ghost" onClick={()=>setDeleteTarget(null)} data-testid="tasks-delete-cancel">{t("tasks.cancel")}</Button>
            <Button className="rounded-full bg-rose-600 hover:bg-rose-700" onClick={confirmDelete} data-testid="tasks-delete-confirm">{t("tasks.delete")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TaskRow({ t, tr, onSetStatus, onDelete, onEdit, archived }) {
  const catEmoji = (TASK_CATEGORIES.find((c) => c.id === t.category) || TASK_CATEGORIES.find((c) => c.id === "general")).emoji;
  const catLabel = tr(`taskCategories.${t.category}`);
  const prioColors = { 1: "#B4B8C0", 2: "#9AA0AC", 3: "#8A94A6", 4: "#C6A24E", 5: "#B4696B" };
  const prio = t.priority || 3;
  return (
    <li className="surface-card btn-lift p-4 flex items-center gap-3" data-testid={`task-row-${t.id}`}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="h-9 w-9 rounded-full border-2 border-slate-300 flex items-center justify-center shrink-0 hover:bg-accent" data-testid={`task-status-${t.id}`} aria-label={tr("tasks.taskActions")}>
            {t.status==="success" ? <Check className="h-4 w-4 text-emerald-600" strokeWidth={3}/> : t.status==="failed" ? <X className="h-4 w-4 text-rose-600" strokeWidth={3}/> : <HelpCircle className="h-4 w-4 text-slate-400"/>}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" data-testid={`task-status-menu-${t.id}`}>
          <DropdownMenuItem onClick={()=>onSetStatus("unknown")} data-testid={`task-status-unknown-${t.id}`}>{tr("tasks.menuUnknown")}</DropdownMenuItem>
          <DropdownMenuItem onClick={()=>onSetStatus("success")} data-testid={`task-status-success-${t.id}`}>{tr("tasks.menuSuccess")}</DropdownMenuItem>
          <DropdownMenuItem onClick={()=>onSetStatus("failed")} data-testid={`task-status-failed-${t.id}`}>{tr("tasks.menuFailed")}</DropdownMenuItem>
          <DropdownMenuItem onClick={onDelete} className="text-rose-600 focus:text-rose-600" data-testid={`task-status-delete-${t.id}`}>{tr("tasks.menuDelete")}</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <button onClick={onEdit} className="flex-1 text-left min-w-0" data-testid={`task-open-${t.id}`}>
        <p className={["text-sm font-medium truncate", archived?"text-slate-500":"text-slate-900"].join(" ")} title={catLabel}>{catEmoji} {t.title}</p>
        <div className="flex flex-wrap items-center gap-2 mt-1.5">
          <span className="inline-flex items-center text-[10px] bg-card border border-border text-slate-600 rounded-full px-2 py-0.5 font-mono-tab"><CalendarDays className="h-3 w-3 mr-1"/>{fmtDate(t.targetDate)}</span>
          <span className="inline-flex items-center gap-1.5 text-[10px] text-slate-500" title={`${tr("createTask.priority")}: ${prio}`} data-testid={`task-priority-${t.id}`}>
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: prioColors[prio] || "#8A94A6" }} />
            {prio}
          </span>
        </div>
      </button>
    </li>
  );
}
